import { Building2, Handshake, Home, Settings, Users } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getCurrentTab, SPONSORING_TABS } from "./currentTab";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  const currentPath = getCurrentTab(location.pathname);
  // The settings tab is the hub of the admin pages, so it stays highlighted on
  // every page reached from it (fasen, users, contract types, profile).
  const isSettings = [
    "/settings",
    "/sales",
    "/contract_types",
    "/profile",
  ].some((path) => !!matchPath({ path, end: false }, location.pathname));

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);
  const extraPadding = isPwa && isWebiOS;

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area
        // inset is not accounted for, so add the padding (and height) manually.
        paddingBottom: extraPadding ? 15 : undefined,
        height: extraPadding ? "calc(3.5rem + 15px)" : undefined,
      }}
    >
      <div className="flex justify-around items-stretch h-14">
        <NavigationButton
          href="/"
          Icon={Home}
          label={translate("ra.page.dashboard")}
          isActive={currentPath === "/"}
        />
        <NavigationButton
          href="/sponsorings"
          Icon={Handshake}
          label={translate("resources.sponsorings.name", { smart_count: 2 })}
          // The three sponsoring overviews share this tab; the segmented
          // control on the page itself switches between them.
          isActive={
            currentPath !== false && SPONSORING_TABS.includes(currentPath)
          }
        />
        <NavigationButton
          href="/companies"
          Icon={Building2}
          label={translate("resources.companies.name", { smart_count: 2 })}
          isActive={currentPath === "/companies"}
        />
        <NavigationButton
          href="/contacts"
          Icon={Users}
          label={translate("resources.contacts.name", { smart_count: 2 })}
          isActive={currentPath === "/contacts"}
        />
        <NavigationButton
          href="/settings"
          Icon={Settings}
          label={translate("crm.settings.title")}
          isActive={isSettings}
        />
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-0.5 h-auto py-1.5 px-1 rounded-md w-16",
      isActive ? "text-brand-blue" : "text-muted-foreground",
    )}
  >
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className="size-5" />
      <span className="text-[0.6rem] font-medium leading-tight text-center">
        {label}
      </span>
    </Link>
  </Button>
);
